// Captain canon 2026-04-26: every authenticated page uses the same
// rail + main grid as the workspace room. SidebarShell is the
// minimum viable shared layout — left column carries the workspace
// rail (160px); right column scrolls the page's main content.
//
// WorkspaceRoomPage keeps its specialised RailTree (it manages
// per-workspace mutation state inline) but matches dimensions; this
// shell is for the lighter pages — HomePage, SettingsPage,
// UserProfilePage, BillingReturnPage.

import { useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Network } from "lucide-react";
import { ChromeCorners } from "./ChromeCorners";
import { ApiError, extractRows, listOrgWorkspaces, listOrgs } from "../api/client";

interface OrgRow {
  org_id: number;
  slug: string;
  name: string;
}

interface WorkspaceRow {
  workspace_id: number;
  org_id: number;
  slug: string;
  name: string;
}

interface Props {
  /** The active workspace_id when the page is workspace-scoped, so
   * the rail can highlight it. 0 = not workspace-scoped. */
  activeWorkspaceId?: number;
  children: ReactNode;
}

const COLUMN_WIDTH_PX = 160;

export function SidebarShell({ activeWorkspaceId = 0, children }: Props) {
  const [org, setOrg] = useState<OrgRow | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const onOrchestrator = location.pathname === "/orchestrator";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const orgs = await listOrgs();
        const list = extractRows(orgs)
          .map((row) => {
            const org_id =
              typeof row[0] === "number" ? row[0] : Number(row[0]);
            const slug = typeof row[1] === "string" ? row[1] : "";
            const name = typeof row[2] === "string" ? row[2] : "";
            if (!org_id || !slug || !name) return null;
            return { org_id, slug, name };
          })
          .filter((o): o is OrgRow => o !== null);
        if (!cancelled && list[0]) {
          setOrg(list[0]);
          const wRes = await listOrgWorkspaces(list[0].org_id);
          if (cancelled) return;
          const wList = extractRows(wRes)
            .map((row) => {
              const workspace_id =
                typeof row[0] === "number" ? row[0] : Number(row[0]);
              const org_id =
                typeof row[1] === "number" ? row[1] : Number(row[1]);
              const slug = typeof row[2] === "string" ? row[2] : "";
              const name = typeof row[3] === "string" ? row[3] : "";
              if (!workspace_id || !org_id || !slug || !name) return null;
              return { workspace_id, org_id, slug, name };
            })
            .filter((w): w is WorkspaceRow => w !== null);
          setWorkspaces(wList);
        }
      } catch (err) {
        // Silent — the page's own error toasts surface failures.
        if (err instanceof ApiError) {
          // Ignore; pages can re-fetch + show their own messaging.
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="h-screen overflow-hidden text-primary transition-colors">
      <ChromeCorners />
      <main
        className="grid h-screen"
        style={{ gridTemplateColumns: `${COLUMN_WIDTH_PX}px 1fr` }}
      >
        <aside
          className="relative flex min-h-0 min-w-0 flex-col overflow-hidden bg-transparent"
          data-testid="sidebar-rail"
        >
          {/* Org / workspace name in the top 56-px header slot.
              Centered horizontally to mirror the bottom brand
              cluster. break-all lets long emails (e.g. dev
              captain+suffix@…) break mid-word so the rail's narrow
              140-px content width is used proportionally — without
              break-all, line-clamp-2 truncates at the first space
              (which never appears in an email) and the second line
              never appears. text-sm + leading-tight + 2-line clamp
              fits the 56-px slot with breathing room. */}
          <div className="relative flex h-14 min-w-0 flex-col items-center justify-center border-b border-edge px-2.5">
            <h2
              className="line-clamp-2 text-center text-sm font-semibold leading-tight break-all text-primary"
              title={org?.name ?? "Workspaces"}
              data-testid="sidebar-org"
            >
              {org?.name ?? "Workspaces"}
            </h2>
          </div>
          <ul
            className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-y-auto px-1.5 pt-1 pb-3"
            data-testid="sidebar-workspaces"
          >
            <li className="mb-1 border-b border-edge pb-1">
              <button
                type="button"
                onClick={() => navigate("/orchestrator")}
                className={`flex w-full cursor-pointer items-center gap-1.5 px-1.5 py-1 text-left text-[0.8125rem] font-medium transition-colors ${
                  onOrchestrator
                    ? "text-accent"
                    : "text-primary hover:text-accent"
                }`}
                title="Orchestrator"
                data-testid="sidebar-orchestrator"
              >
                <Network size={12} className="shrink-0 text-muted" />
                <span className="truncate">orchestrator</span>
              </button>
            </li>
            {workspaces.map((w) => {
              const active = w.workspace_id === activeWorkspaceId;
              return (
                <li key={w.workspace_id} className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => navigate(`/workspaces/${w.workspace_id}`)}
                    className={`flex-1 cursor-pointer truncate px-1.5 py-0.5 text-left text-[0.8125rem] font-medium transition-colors ${
                      active
                        ? "text-accent"
                        : "text-primary hover:text-accent"
                    }`}
                    title={w.name}
                    data-testid={`sidebar-workspace-${w.slug}`}
                  >
                    {w.name}
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>
        <section
          className="relative h-screen overflow-y-auto"
          data-testid="sidebar-shell-main"
        >
          {children}
        </section>
      </main>
    </div>
  );
}
